import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Send, Sparkles, EyeOff, Undo2, Check, AlertTriangle } from 'lucide-react';
import { getAccessToken } from '../services/auth';

// The function of the same name on this site passes the call through to CNCx with the
// signed-in person's own session (netlify/functions/google-review-replies.js).
const base = window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions'
  : '/.netlify/functions';

async function googleRepliesApi(action, params = {}) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/google-review-replies`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ action, ...params }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || `${action} failed (${res.status})`), { code: data.code });
  return data;
}

/**
 * GoogleReplies - Crab & Cruise's Google reviews, answered from CPx.
 *
 * COPIED FROM cncx/src/pages/GoogleReplies.js (Rachael, 2026-10-09: customer service could
 * not sign in to CNCx, where this lived, so the review email was a dead end for the people
 * it is sent to). The queue is the same queue: both screens call the one CNCx function.
 * Keep the two in step. The one-time Google setup stays on CNCx and is not here.
 *
 * Claude drafts a reply to each new Google review; a person reads it, edits it if they
 * want, and presses Post. Nothing reaches Google without that click.
 *
 * ⚠️ POSTING IS PUBLIC AND IMMEDIATE, under the business's name, so Post asks once before
 * it sends and the button is disabled while it is in flight.
 */

const NAVY = 'var(--navy, #003b5c)';
const ORANGE = 'var(--orange, #dc582a)';
const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16 };
const btn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: NAVY };
const primary = { ...btn, background: ORANGE, borderColor: ORANGE, color: '#fff' };
const note = { margin: '6px 0 0', color: '#6b7280', fontSize: 13 };

const day = (ts) => ts ? new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

function ReviewRow({ r, canDraft, onChanged, onError }) {
  const [text, setText] = useState(r.draft || '');
  const [busy, setBusy] = useState('');
  const [ask, setAsk] = useState('');
  const saved = useRef(r.draft || '');
  const low = r.rating <= 3;
  const skipped = r.status === 'skipped';

  const run = async (what, fn) => {
    setBusy(what);
    try { await fn(); } catch (e) { onError(e); } finally { setBusy(''); }
  };

  // Keep an edit if they walk away from the box without posting.
  const saveIfChanged = () => {
    if (text.trim() === saved.current.trim()) return;
    saved.current = text;
    googleRepliesApi('save', { reviewId: r.review_id, text }).catch(() => {});
  };

  const draft = () => run('draft', async () => {
    const out = await googleRepliesApi('draft', { reviewId: r.review_id, note: ask || undefined });
    setText(out.draft); saved.current = out.draft; setAsk('');
  });

  const post = () => {
    if (!window.confirm(`Post this reply on Google under ${r.author_name}'s review? It is public straight away.`)) return;
    run('post', async () => {
      await googleRepliesApi('post', { reviewId: r.review_id, text });
      onChanged();
    });
  };

  const skip = (on) => run('skip', async () => {
    await googleRepliesApi(on ? 'skip' : 'unskip', { reviewId: r.review_id });
    onChanged();
  });

  return (
    <div style={{ borderTop: '1px solid #eef2f6', marginTop: 14, paddingTop: 14, opacity: skipped ? 0.65 : 1 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <b style={{ color: NAVY, fontSize: 14 }}>{r.author_name}</b>
        <span style={{ color: low ? '#b91c1c' : ORANGE, fontSize: 15, letterSpacing: 1 }}>
          {'★'.repeat(r.rating || 0)}{'☆'.repeat(5 - (r.rating || 0))}
        </span>
        <span style={{ color: '#6b7280', fontSize: 13 }}>{day(r.review_time)}</span>
        {low && !skipped && (
          <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 700, color: '#991b1b', background: '#fee2e2' }}>
            <AlertTriangle size={11} style={{ verticalAlign: -1 }} /> Reply to this one first
          </span>
        )}
      </div>
      <p style={{ margin: '0 0 10px', fontSize: 14, lineHeight: 1.55, color: '#1f2937', whiteSpace: 'pre-wrap' }}>
        {r.review_text || <span style={{ color: '#6b7280' }}>Stars only, no written review.</span>}
      </p>

      {skipped ? (
        <button style={btn} disabled={!!busy} onClick={() => skip(false)}><Undo2 size={14} /> Put back in the list</button>
      ) : (
        <>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={saveIfChanged}
            rows={5}
            placeholder={canDraft ? 'No draft yet. Press Draft a reply, or type your own.' : 'Type the reply here.'}
            style={{ width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, lineHeight: 1.5, fontFamily: 'inherit', resize: 'vertical', background: '#fffdf8' }}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <button style={{ ...primary, opacity: !text.trim() || busy ? 0.55 : 1 }} disabled={!text.trim() || !!busy} onClick={post}>
              <Send size={14} /> {busy === 'post' ? 'Posting...' : 'Post reply on Google'}
            </button>
            {canDraft && (
              <>
                <button style={btn} disabled={!!busy} onClick={draft}>
                  <Sparkles size={14} /> {busy === 'draft' ? 'Writing...' : text.trim() ? 'Write another draft' : 'Draft a reply'}
                </button>
                <input
                  value={ask}
                  onChange={(e) => setAsk(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && ask.trim()) draft(); }}
                  placeholder="Optional: tell Claude what to change"
                  style={{ flex: '1 1 220px', minWidth: 180, padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}
                />
              </>
            )}
            <button style={{ ...btn, color: '#6b7280' }} disabled={!!busy} onClick={() => skip(true)} title="Hide this review; no reply will be posted">
              <EyeOff size={14} /> No reply needed
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function GoogleReplies() {
  const [st, setSt] = useState(null);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [busy, setBusy] = useState('');
  const [showSkipped, setShowSkipped] = useState(false);
  const [gen, setGen] = useState(0);            // remounts rows after a reload so drafts refresh
  const [progress, setProgress] = useState(null);

  const load = useCallback(() => googleRepliesApi('status')
    .then((s) => { setSt(s); setGen((n) => n + 1); })
    .catch((e) => setErr(e.message)), []);

  useEffect(() => {
    load();
  }, [load]);

  const fail = (e) => {
    setOk('');
    setErr(e.message);
    if (e.code === 'reconnect' || e.code === 'not_connected') setSt((s) => s && { ...s, connected: false });
  };

  const sync = () => {
    setBusy('sync'); setErr(''); setOk('');
    googleRepliesApi('sync')
      .then((s) => {
        setSt(s); setGen((n) => n + 1);
        setOk(`Checked Google: ${s.sync.added} new, ${s.drafts.drafted || 0} drafted.`);
      })
      .catch(fail)
      .finally(() => setBusy(''));
  };

  // Draft every review that has none, a batch at a time, until the server says none are
  // left. Stops if a batch drafts nothing, so a Claude outage cannot spin forever.
  const draftAll = async (n) => {
    setBusy('all'); setErr(''); setOk(''); setProgress({ done: 0, of: n });
    let done = 0;
    try {
      for (;;) {
        const r = await googleRepliesApi('draft_all');
        done += r.drafted || 0;
        setProgress({ done, of: n });
        if (!r.remaining) break;
        if (!r.drafted) throw new Error((r.errors && r.errors[0]) || 'Drafting stopped early. Try again in a minute.');
      }
      setOk(`${done} replies drafted. Read each one, then post.`);
    } catch (e) { fail(e); }
    setProgress(null); setBusy('');
    load();
  };

  if (!st && !err) return <div style={{ padding: 20, color: '#6b7280', fontSize: 13 }}>Loading the reviews...</div>;

  const waiting = (st && st.waiting) || [];
  const open = waiting.filter((r) => r.status !== 'skipped');
  const skipped = waiting.filter((r) => r.status === 'skipped');
  const undrafted = open.filter((r) => !(r.draft || '').trim()).length;

  return (
    <div style={{ padding: 20, maxWidth: 980, margin: '0 auto' }}>
    <h1 style={{ margin: '0 0 4px', fontSize: 22, color: NAVY }}>Google Review Replies</h1>
    <p style={{ margin: '0 0 16px', color: '#6b7280', fontSize: 13 }}>Reviews left on the Crab &amp; Cruise Google listing.</p>
    <div style={{ ...card, marginBottom: 18 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        <b style={{ color: NAVY, fontSize: 15 }}>Waiting for a reply</b>
        {st && st.connected && (
          <>
            <span style={{ fontSize: 14, color: open.length ? '#b45309' : '#15803d', fontWeight: 700 }}>
              {open.length ? `${open.length} waiting for a reply` : 'Every review has a reply'}
            </span>
            <span style={{ fontSize: 13, color: '#6b7280' }}>{st.replied} of {st.total} answered</span>
            {st.canDraft && undrafted > 0 && (
              <button style={{ ...primary, marginLeft: 'auto', opacity: busy ? 0.7 : 1 }} disabled={!!busy} onClick={() => draftAll(undrafted)}>
                <Sparkles size={14} /> {progress ? `Drafting ${progress.done} of ${progress.of}...` : `Draft all ${undrafted}`}
              </button>
            )}
            <button style={{ ...btn, marginLeft: st.canDraft && undrafted > 0 ? 0 : 'auto' }} disabled={!!busy} onClick={sync}>
              <RefreshCw size={14} /> {busy === 'sync' ? 'Checking...' : 'Check Google now'}
            </button>
          </>
        )}
      </div>

      {err && <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13 }}>{err}</div>}
      {ok && <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', fontSize: 13 }}><Check size={13} style={{ verticalAlign: -2 }} /> {ok}</div>}

      {st && !st.connected && (
        <p style={note}>Google is not connected right now, so there is nothing to show. Tell Greg.</p>
      )}

      {/* ---------- the queue ---------- */}
      {st && st.connected && (
        <>
          <p style={note}>
            {st.canDraft
              ? 'Claude drafts a reply to each new review. Read it, change anything you like, then post. Nothing goes to Google until you press Post.'
              : 'Drafting is not switched on yet, so type each reply yourself for now. Posting works.'}
          </p>

          {open.map((r) => (
            <ReviewRow key={r.review_id + ':' + gen} r={r} canDraft={st.canDraft} onChanged={load} onError={fail} />
          ))}

          {skipped.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <button style={{ ...btn, border: 'none', padding: 0, color: '#6b7280' }} onClick={() => setShowSkipped((v) => !v)}>
                {showSkipped ? 'Hide' : 'Show'} {skipped.length} marked "no reply needed"
              </button>
              {showSkipped && skipped.map((r) => (
                <ReviewRow key={r.review_id + ':' + gen} r={r} canDraft={st.canDraft} onChanged={load} onError={fail} />
              ))}
            </div>
          )}

          {(st.recent || []).length > 0 && (
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #eef2f6' }}>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: '#6b7280' }}>Latest replies on Google</div>
              {st.recent.map((r) => (
                <div key={r.review_id} style={{ marginTop: 10, fontSize: 13 }}>
                  <b>{r.author_name}</b> <span style={{ color: ORANGE }}>{'★'.repeat(r.rating || 0)}</span>
                  <span style={{ color: '#6b7280' }}> · replied {day(r.google_reply_time)}{r.posted_by ? ` by ${r.posted_by}` : ''}</span>
                  <div style={{ color: '#374151', marginTop: 2, whiteSpace: 'pre-wrap' }}>{r.google_reply}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
    </div>
  );
}
